export enum Colors {
  colorPrimary = 'colorPrimary',
  colorPrimaryLight = 'colorPrimaryLight',
  colorPrimaryDark = 'colorPrimaryDark',
  colorPrimaryDarker = 'colorPrimaryDarker',
  colorBrandError = 'colorBrandError',
  colorBrandWarn = 'colorBrandWarn',
  colorBrandWhite = 'colorBrandWhite',
  colorGray = 'colorGray',
  colorGrayLight = 'colorGrayLight',
  colorGrayLighter = 'colorGrayLighter',
  colorGrayDark = 'colorGrayDark',
  colorGrayDarker = 'colorGrayDarker',
  colorFont = 'colorFont',
  colorLogoBackground = 'colorLogoBackground',
  colorWhiteLabelBackground = 'colorWhiteLabelBackground',
  colorBlack = 'colorBlack',
  colorWhiteLabelSiteHeader = 'colorWhiteLabelSiteHeader',
  colorAccent = 'colorAccent',
  /** Darkened accent, for text/icons sitting ON an accent tint. */
  colorAccentDark = 'colorAccentDark',
}

/**
 * Maps Colors enum to Tailwind text color classes.
 * Uses CSS variables set by themeInjector.
 */
export const textColorClasses: Record<Colors, string> = {
  [Colors.colorPrimary]: 'tw:text-primary',
  [Colors.colorPrimaryLight]: 'tw:text-primary-light',
  [Colors.colorPrimaryDark]: 'tw:text-primary-dark',
  [Colors.colorPrimaryDarker]: 'tw:text-primary-darker',
  [Colors.colorBrandError]: 'tw:text-error',
  [Colors.colorBrandWarn]: 'tw:text-warn',
  [Colors.colorBrandWhite]: 'tw:text-white',
  [Colors.colorGray]: 'tw:text-gray',
  [Colors.colorGrayLight]: 'tw:text-gray-light',
  [Colors.colorGrayLighter]: 'tw:text-gray-lighter',
  [Colors.colorGrayDark]: 'tw:text-gray-dark',
  [Colors.colorGrayDarker]: 'tw:text-gray-darker',
  [Colors.colorFont]: 'tw:text-font',
  [Colors.colorLogoBackground]: 'tw:text-logo-background',
  [Colors.colorWhiteLabelBackground]: 'tw:text-whitelabel-background',
  [Colors.colorBlack]: 'tw:text-black',
  [Colors.colorWhiteLabelSiteHeader]: 'tw:text-whitelabel-header',
  [Colors.colorAccent]: 'tw:text-accent',
  [Colors.colorAccentDark]: 'tw:text-accent-dark',
};

/**
 * Maps Colors enum to Tailwind background color classes.
 * Uses CSS variables set by themeInjector.
 */
export const bgColorClasses: Record<Colors, string> = {
  [Colors.colorPrimary]: 'tw:bg-primary',
  [Colors.colorPrimaryLight]: 'tw:bg-primary-light',
  [Colors.colorPrimaryDark]: 'tw:bg-primary-dark',
  [Colors.colorPrimaryDarker]: 'tw:bg-primary-darker',
  [Colors.colorBrandError]: 'tw:bg-error',
  [Colors.colorBrandWarn]: 'tw:bg-warn',
  [Colors.colorBrandWhite]: 'tw:bg-white',
  [Colors.colorGray]: 'tw:bg-gray',
  [Colors.colorGrayLight]: 'tw:bg-gray-light',
  [Colors.colorGrayLighter]: 'tw:bg-gray-lighter',
  [Colors.colorGrayDark]: 'tw:bg-gray-dark',
  [Colors.colorGrayDarker]: 'tw:bg-gray-darker',
  [Colors.colorFont]: 'tw:bg-font',
  [Colors.colorLogoBackground]: 'tw:bg-logo-background',
  [Colors.colorWhiteLabelBackground]: 'tw:bg-whitelabel-background',
  [Colors.colorBlack]: 'tw:bg-black',
  [Colors.colorWhiteLabelSiteHeader]: 'tw:bg-whitelabel-header',
  [Colors.colorAccent]: 'tw:bg-accent',
  [Colors.colorAccentDark]: 'tw:bg-accent-dark',
};

// ---------------------------------------------------------------------------
// Contrast (WCAG 1.4.3)
// ---------------------------------------------------------------------------

/** WCAG AA threshold for normal-size text. */
export const AA_CONTRAST = 4.5;

const hexToRgb = (hex: string): [number, number, number] | null => {
  const h = hex.trim().replace('#', '');
  const full = h.length === 3 ? h.replace(/./g, (c) => c + c) : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
};

/** Relative luminance per WCAG 2.x. */
const luminance = (rgb: [number, number, number]): number => {
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Contrast ratio between two hex colours, 1–21. Returns null when either cannot be parsed.
 */
export const getContrastRatio = (a: string, b: string): number | null => {
  const [ra, rb] = [hexToRgb(a), hexToRgb(b)];
  if (!ra || !rb) return null;
  const [hi, lo] = [luminance(ra), luminance(rb)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/** hex -> [h, s, l]; h in turns, s/l in 0..1. */
const hexToHsl = (hex: string): [number, number, number] | null => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
};

const hslToHex = (h: number, s: number, l: number): string => {
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)))));
  };
  return `#${[f(0), f(8), f(4)]
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
};

/**
 * Darkens `background` - hue and saturation untouched, lightness only - until `foreground`
 * reaches `target` contrast against it.
 *
 * Adjusting lightness rather than substituting a colour is how this codebase already derives
 * from configured colours: see the `dot-gradient` utility in globals.css, which mixes in HSL
 * "so it is never a hardcoded green or red". It keeps the brand hue recognisable, where a
 * hand-picked replacement would not (PB-5610).
 *
 * ## Invariants:
 * - Returns `background` unchanged when it already passes. Why: colours that are fine must not
 *   be darkened for the sake of it, or every themed surface shifts.
 * - Steps lightness DOWN only. Why: the caller is placing light text on this colour, so
 *   lightening moves away from the threshold and the loop would run to the floor.
 * - Gives up at 5% lightness and returns the darkest attempt rather than looping forever. A
 *   fully saturated hue cannot always reach 4.5:1 against white at any lightness.
 */
export const darkenToContrast = (
  background: string,
  foreground: string = '#FFFFFF',
  target: number = AA_CONTRAST
): string => {
  const hsl = hexToHsl(background);
  if (!hsl) return background;
  if ((getContrastRatio(background, foreground) ?? 0) >= target) return background;

  const [h, s] = hsl;
  let l = hsl[2];
  let out = background;
  while (l > 0.05) {
    l -= 0.005;
    out = hslToHex(h, s, l);
    if ((getContrastRatio(out, foreground) ?? 0) >= target) return out;
  }
  return out;
};
